import { Fragment, useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { TwoSidedWarningIcon } from "./TwoSidedWarningIcon";

const formatTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const formatDollars = (v) => (v === null || v === undefined ? "--" : `$${Math.round(v).toLocaleString()}`);
const formatPrice = (v) => (v === null || v === undefined ? "--" : `${Math.round(v * 100)}c`);
const formatVolume = (v) => (v === null || v === undefined ? "--" : Math.round(v).toLocaleString());

const SideBadge = ({ side }) => {
  const isYes = side === "YES";
  return (
    <span
      className={`whitespace-nowrap rounded-full border px-2 py-0.5 font-mono text-[11px] font-semibold uppercase ${
        isYes ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-400" : "border-red-400/40 bg-red-400/10 text-red-400"
      }`}
    >
      Large {side} Trade
    </span>
  );
};

const TypeBadge = ({ row }) => (
  <span
    data-testid={`kalshi-row-${row.trade_id}-type`}
    className={`whitespace-nowrap rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase ${
      row.is_parlay ? "border-amber-400/40 bg-amber-400/10 text-amber-400" : "border-zinc-600/50 bg-zinc-600/10 text-zinc-400"
    }`}
  >
    {row.is_parlay ? `Parlay \u00b7 ${row.leg_count} legs` : "Single"}
  </span>
);

const LegRow = ({ leg }) => (
  <tr data-testid={`kalshi-leg-${leg.market_ticker}`} className="border-b border-white/5 text-zinc-400 last:border-0">
    <td className="px-3 py-2 uppercase text-zinc-500">{leg.sport_bucket || "--"}</td>
    <td className="px-3 py-2 uppercase text-zinc-500">{leg.league_label || "Unknown League"}</td>
    <td className="px-3 py-2 text-zinc-200">{leg.matchup || "Matchup unavailable"}</td>
    <td className="px-3 py-2">{leg.selection_title || "--"}</td>
    <td className="px-3 py-2">
      <span className={leg.side === "YES" ? "font-semibold text-emerald-400" : "font-semibold text-red-400"}>{leg.side || "--"}</span>
    </td>
    <td className="px-3 py-2 text-zinc-500">{leg.start_verified ? formatTime(leg.verified_start_utc) : "Start time unverified"}</td>
  </tr>
);

const ParlayLegs = ({ legs, tradeId }) => (
  <div data-testid={`kalshi-parlay-legs-${tradeId}`} className="overflow-x-auto px-3 py-3">
    <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-zinc-500">
      Every leg's own selection -- the trade's overall YES/NO above is the parlay's combined side, not each leg's individual side.
    </p>
    <table className="w-full font-mono text-[11px]">
      <thead>
        <tr className="border-b border-white/10 text-left text-zinc-500">
          <th className="px-3 py-2">Sport</th>
          <th className="px-3 py-2">League</th>
          <th className="px-3 py-2">Matchup</th>
          <th className="px-3 py-2">Leg Selection</th>
          <th className="px-3 py-2">Leg Side</th>
          <th className="px-3 py-2">Event Start</th>
        </tr>
      </thead>
      <tbody>
        {legs.map((leg) => (
          <LegRow key={leg.market_ticker} leg={leg} />
        ))}
      </tbody>
    </table>
  </div>
);

const SportBadge = ({ row }) => (
  <span
    data-testid={`kalshi-row-${row.trade_id}-sport`}
    className="whitespace-nowrap rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase text-zinc-500"
  >
    {row.is_mixed_sport ? `Mixed \u00b7 ${(row.sport_buckets || []).join(", ")}` : row.sport_buckets?.[0] || "--"}
  </span>
);

const MarketTypeBadge = ({ row }) => (
  <span
    data-testid={`kalshi-row-${row.trade_id}-market-type`}
    className={`ml-1 whitespace-nowrap rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase ${
      row.market_type === "player_prop" ? "border-sky-400/40 text-sky-400" : "border-white/10 text-zinc-600"
    }`}
  >
    {row.market_type === "player_prop" ? "Prop" : "Line"}
  </span>
);

export const BigMoneyTable = ({ rows, warnings }) => {
  const [expanded, setExpanded] = useState(null);

  if (rows.length === 0) {
    return (
      <p data-testid="kalshi-table-empty" className="rounded-lg border border-dashed border-zinc-800 bg-eb-surface/40 px-6 py-10 text-center font-mono text-xs text-zinc-600">
        No upcoming Kalshi trades match this filter yet.
      </p>
    );
  }

  const warningByTicker = new Map((warnings || []).map((w) => [w.ticker, w]));

  return (
    <div className="overflow-x-auto rounded-lg border border-white/10 bg-eb-surface">
      <table data-testid="kalshi-big-money-table" className="w-full font-mono text-xs">
        <thead>
          <tr className="border-b border-white/10 text-left text-zinc-500">
            <th className="px-3 py-2"></th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Sport</th>
            <th className="px-3 py-2">Market</th>
            <th className="px-3 py-2">Side</th>
            <th className="px-3 py-2">Execution Price</th>
            <th className="px-3 py-2">Contracts</th>
            <th className="px-3 py-2">Estimated $</th>
            <th className="px-3 py-2">Event Start</th>
            <th className="px-3 py-2">Trade Time</th>
            <th className="px-3 py-2">24h Volume</th>
            <th className="px-3 py-2">Liquidity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOpen = expanded === r.trade_id;
            return (
              <Fragment key={r.trade_id}>
                <tr
                  data-testid={`kalshi-row-${r.trade_id}`}
                  onClick={() => r.is_parlay && setExpanded(isOpen ? null : r.trade_id)}
                  className={`border-b border-white/5 text-zinc-300 last:border-0 ${r.is_parlay ? "cursor-pointer transition-colors hover:bg-white/[0.03]" : ""}`}
                >
                  <td className="px-3 py-2 text-zinc-500">
                    {r.is_parlay ? (isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />) : null}
                  </td>
                  <td className="px-3 py-2">
                    <TypeBadge row={r} />
                  </td>
                  <td className="px-3 py-2">
                    <SportBadge row={r} />
                    <MarketTypeBadge row={r} />
                  </td>
                  <td className="max-w-[520px] px-3 py-2 text-white">
                    {r.is_parlay ? (
                      <div className="text-zinc-300">
                        Parlay across {r.leg_count} legs
                        {r.league_label && !r.is_mixed_sport && <span className="text-zinc-500"> &middot; {r.league_label}</span>}
                      </div>
                    ) : (
                      <div className="flex items-start gap-1.5">
                        <div className="min-w-0 flex-1" title={[r.event_title, r.market_title].filter(Boolean).join(" — ")}>
                          {r.league_label && (
                            <span className="mr-1.5 rounded border border-white/10 px-1 py-0.5 text-[9px] uppercase text-zinc-500">{r.league_label}</span>
                          )}
                          {r.event_title && <div className="truncate text-[11px] font-semibold text-zinc-400">{r.event_title}</div>}
                          <div className="truncate">{r.market_title}</div>
                        </div>
                        <TwoSidedWarningIcon warning={warningByTicker.get(r.ticker)} />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <SideBadge side={r.side} />
                  </td>
                  <td className="px-3 py-2">{formatPrice(r.execution_price)}</td>
                  <td className="px-3 py-2">{formatVolume(r.contracts)}</td>
                  <td className="px-3 py-2 font-semibold text-eb-accent" data-testid={`kalshi-row-${r.trade_id}-dollars`}>
                    {formatDollars(r.estimated_dollars)}
                  </td>
                  <td className="px-3 py-2 text-zinc-400" data-testid={`kalshi-row-${r.trade_id}-start`}>
                    {r.start_verified ? formatTime(r.verified_start_utc) : "--"}
                  </td>
                  <td className="px-3 py-2 text-zinc-500">{formatTime(r.created_time)}</td>
                  <td className="px-3 py-2 text-zinc-500">{formatVolume(r.market_volume_24h)}</td>
                  <td className="px-3 py-2 text-zinc-500">{formatDollars(r.liquidity_dollars)}</td>
                </tr>
                {isOpen && r.is_parlay && (
                  <tr className="border-b border-white/5 bg-eb-bg/60 last:border-0">
                    <td colSpan={12}>
                      <ParlayLegs legs={r.legs} tradeId={r.trade_id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
